import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { showToast } from '@mercai/clever'

import { QUERIES_ADMIN, useSupplyPaymentRequired } from '@/services'
import { useQueryClient } from '@tanstack/react-query'

import { SupplyOrderProps, SupplyPaymentRequiredPayload } from '@/types'

import { TableSupplyOrder } from '../components'
import { SupplyPaymentRequiredModal } from './components'

export const TabPurchaseOrder = () => {
  const { t } = useTranslation('screenSupplierOrderManagementPage')

  const [checkedSupplyOrders, setCheckedSupplyOrders] = useState<
    SupplyOrderProps[]
  >([])

  const checkedSupplyOrdersUuids = useMemo(
    () => checkedSupplyOrders.map((supplyOrder) => supplyOrder.uuid || ''),
    [checkedSupplyOrders],
  )

  const queryClient = useQueryClient()

  const { mutateAsync, isPending } = useSupplyPaymentRequired()

  const onHandleToggleSupplyOrder = (supplyOrder: SupplyOrderProps) => {
    if (checkedSupplyOrdersUuids.includes(supplyOrder.uuid || '')) {
      setCheckedSupplyOrders((oldCheckedSupplyOrders) =>
        oldCheckedSupplyOrders.filter((item) => item.uuid !== supplyOrder.uuid),
      )
    } else {
      setCheckedSupplyOrders((oldCheckedSupplyOrders) => [
        ...oldCheckedSupplyOrders,
        supplyOrder,
      ])
    }
  }

  const onSubmit = async (
    values: Omit<SupplyPaymentRequiredPayload, 'supplyOrdersReference'>,
  ) => {
    if (!checkedSupplyOrders.length) return false

    try {
      const response = await mutateAsync({
        ...values,
        supplyOrdersReference: checkedSupplyOrders.map(
          (supplyOrder) => supplyOrder.reference || '',
        ),
      })

      if (!response.success) {
        showToast({
          variant: 'error',
          title: response?.message || t('supplyPaymentRequiredErrorTitle'),
        })

        return false
      }

      queryClient.refetchQueries({
        queryKey: [QUERIES_ADMIN.SUPPLIER_ORDER.module],
      })

      showToast({
        variant: 'success',
        title: t('supplyPaymentRequiredSuccessTitle'),
      })

      setCheckedSupplyOrders([])

      return true
    } catch (error) {
      showToast({
        variant: 'error',
        title: t('supplyPaymentRequiredErrorTitle'),
      })

      return false
    }
  }

  return (
    <>
      <TableSupplyOrder
        selectedSupplierOrders={checkedSupplyOrdersUuids}
        onSelectedSupplierOrdersChange={onHandleToggleSupplyOrder}
        filters={{ paymentStatusExclude: 'payment_required,cancelled' }}
        hiddenAppliedFilters={['paymentStatusExclude']}
      />

      <SupplyPaymentRequiredModal
        isOpen={checkedSupplyOrdersUuids?.length > 0}
        isLoading={isPending}
        onSupplyPaymentRquired={onSubmit}
      />
    </>
  )
}
